import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-type text-[11px] uppercase tracking-wider text-[#5A7355]">{label}</Label>
    {children}
  </div>
);

const Submit = ({ busy, children, testId }) => (
  <button type="submit" disabled={busy} data-testid={testId} className="w-full h-11 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2">
    {busy && <Loader2 className="w-4 h-4 animate-spin" />} {children}
  </button>
);

export const EditProfileDialog = ({ open, onOpenChange, member, onSaved }) => {
  const [name, setName] = useState(member.name);
  const [postcode, setPostcode] = useState(member.postcode || "");
  const [options, setOptions] = useState(null);
  const [electorate, setElectorate] = useState("");
  const [busy, setBusy] = useState(false);
  const willMove = options || (postcode.padStart(4, "0") !== (member.postcode || "").padStart(4, "0"));

  const submit = async (e) => {
    e.preventDefault();
    if (options && !electorate) return toast.error("Choose your electorate");
    setBusy(true);
    try {
      const { data } = await api.put("/member/profile", { name, postcode, electorate: electorate || undefined });
      if (data.needs_selection) {
        setOptions(data.electorates);
        return;
      }
      toast.success(data.moved ? `You now carry ${data.member.electorate} — new member number ${data.member.member_number}` : "Profile updated");
      import("@/lib/firebase").then((fb) => fb.firebaseEnabled && fb.syncRoanProfile(fb.fbAuth().currentUser, data.member)).catch(() => {});
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-profile-dialog" className="bg-[#FDFBF7] text-[#1E2830] border-[#1B2A41]/10">
        <DialogHeader>
          <DialogTitle className="font-type uppercase tracking-wider text-[#1B2A41]">Edit profile</DialogTitle>
          <DialogDescription>Change your name or postcode. A new postcode in a different electorate moves you there and issues a new member number.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Full name"><Input data-testid="edit-profile-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="h-11" /></Field>
          <Field label="Postcode"><Input data-testid="edit-profile-postcode" required inputMode="numeric" pattern="[0-9]{3,4}" value={postcode} onChange={(e) => { setPostcode(e.target.value); setOptions(null); setElectorate(""); }} className="h-11" /></Field>
          {options && (
            <Field label="That postcode spans more than one electorate — pick yours">
              <div className="grid gap-2">
                {options.map((o) => (
                  <button type="button" key={o.name} onClick={() => setElectorate(o.name)} data-testid={`edit-profile-option-${o.name}`} className={`text-left px-4 py-2.5 rounded-lg border text-sm ${electorate === o.name ? "border-[#E2705A] bg-[#E2705A]/10" : "border-[#D8D2C4] hover:border-[#8FA58A]"}`}>
                    {o.name} <span className="text-xs text-[#5C6A79]">({o.state})</span>
                  </button>
                ))}
              </div>
            </Field>
          )}
          {willMove && <p className="text-xs text-[#E2705A]" data-testid="edit-profile-move-warning">Heads up: if this postcode is in a different electorate you'll be moved to that district's next open group and given a new member number. Your old number is retired.</p>}
          <Submit busy={busy} testId="edit-profile-submit">Save changes</Submit>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ChangeEmailDialog = ({ open, onOpenChange, member }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/member/email-change", { new_email: email });
      setSentTo(email);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setSentTo(""); }}>
      <DialogContent data-testid="change-email-dialog" className="bg-[#FDFBF7] text-[#1E2830] border-[#1B2A41]/10">
        <DialogHeader>
          <DialogTitle className="font-type uppercase tracking-wider text-[#1B2A41]">Change email</DialogTitle>
          <DialogDescription>We'll send a confirmation link to the new address. Your email only changes once you click it — until then <strong>{member.email}</strong> stays active.</DialogDescription>
        </DialogHeader>
        {sentTo ? (
          <p data-testid="change-email-sent" className="rounded-xl bg-[#E5EDE3] border border-[#8FA58A]/40 p-4 text-sm">Confirmation sent to <strong>{sentTo}</strong>. Open it on any device and you'll be signed in with the new address.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="New email address"><Input data-testid="change-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" /></Field>
            <Submit busy={busy} testId="change-email-submit">Send confirmation link</Submit>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const SetPasswordDialog = ({ open, onOpenChange, member }) => {
  const { refresh } = useMember();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/member/password", { password: pw });
      await refresh();
      toast.success("Password saved — you can now sign in with email + password");
      onOpenChange(false);
      setPw(""); setPw2("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-password-dialog" className="bg-[#FDFBF7] text-[#1E2830] border-[#1B2A41]/10">
        <DialogHeader>
          <DialogTitle className="font-type uppercase tracking-wider text-[#1B2A41]">{member.has_password ? "Change password" : "Set a password"}</DialogTitle>
          <DialogDescription>Optional. The email link always works too. Minimum 8 characters.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="New password"><Input data-testid="set-password-input" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" /></Field>
          <Field label="Repeat password"><Input data-testid="set-password-repeat" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11" /></Field>
          <Submit busy={busy} testId="set-password-submit">Save password</Submit>
        </form>
      </DialogContent>
    </Dialog>
  );
};
